import { useCallback, useEffect, useRef, useState } from "react";

const TARGET = "MERT KERIMI";

// Key ids pressed while the keyboard types TARGET by itself.
const DEMO = [
  ["m1", "M"],
  ["e1", "E"],
  ["r1", "R"],
  ["t1", "T"],
  ["space", " "],
  ["k2", "K"],
  ["e2", "E"],
  ["r2", "R"],
  ["i2", "I"],
  ["m2", "M"],
  ["i3", "I"],
];

const TYPE_MS = 170;
const FLASH_MS = 110;
const IDLE_MS = 4000;

/**
 * Drives the hero display: types "MERT KERIMI" on mount, lets the visitor
 * type/delete, and after IDLE_MS without a key press types TARGET again.
 */
const useKeyboardDemo = ({ maxLength = 18 } = {}) => {
  const [typed, setTyped] = useState("");
  const [pressed, setPressed] = useState(null);
  const typedRef = useRef("");
  const demoRef = useRef(null);
  const idleRef = useRef(null);
  const flashRef = useRef(null);

  typedRef.current = typed;

  const flash = useCallback((id) => {
    setPressed(id);
    clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setPressed(null), FLASH_MS);
  }, []);

  const startDemo = useCallback(() => {
    clearInterval(demoRef.current);
    clearTimeout(idleRef.current);
    setTyped("");
    let i = 0;
    demoRef.current = setInterval(() => {
      if (i >= DEMO.length) {
        clearInterval(demoRef.current);
        return;
      }
      const [id, ch] = DEMO[i++];
      flash(id);
      setTyped((t) => t + ch);
    }, TYPE_MS);
  }, [flash]);

  // Any visitor key press stops the demo and restarts the idle countdown.
  const touch = useCallback(
    (id) => {
      clearInterval(demoRef.current);
      clearTimeout(idleRef.current);
      idleRef.current = setTimeout(() => {
        if (typedRef.current !== TARGET) startDemo();
      }, IDLE_MS);
      flash(id);
    },
    [flash, startDemo]
  );

  useEffect(() => {
    startDemo();
    return () => {
      clearInterval(demoRef.current);
      clearTimeout(idleRef.current);
      clearTimeout(flashRef.current);
    };
  }, [startDemo]);

  const type = useCallback(
    (id, ch) => {
      touch(id);
      setTyped((t) => (t + ch).slice(-maxLength));
    },
    [touch, maxLength]
  );

  const backspace = useCallback(
    (id) => {
      touch(id);
      setTyped((t) => t.slice(0, -1));
    },
    [touch]
  );

  const clear = useCallback(
    (id) => {
      touch(id);
      setTyped("");
    },
    [touch]
  );

  return { typed, pressed, type, backspace, clear, touch };
};

export default useKeyboardDemo;
